import { useCallback, useMemo, useState } from 'react'
import { Table, Input, Popover } from 'antd'
import {
  CaretRightOutlined,
  CaretDownOutlined,
  PlusOutlined,
  MinusOutlined,
  SearchOutlined,
  CloseOutlined,
  ExclamationCircleFilled,
} from '@ant-design/icons'

import ReactDragListView from '../Drag/ReactDragListView'

import { TranslationKeys } from '../../Constants/TranslationKeys'
import {
  calculateColumnWidthBasedOnText,
  filterGeneralDistinct,
  renderTranslation,
  stopPropagation,
} from '../../Helpers/GenericHelpers'
import { setCapitalLetter, unsensitiveCaseSearchMatch } from '../../Helpers/StringHelpers'
import {
  MarketplaceCategory,
  MarketplaceStoreName,
  DefaultOrderCategoryWebsite,
  DefaultOrderCategoryLogistic,
  MktTableColumnTranslationPrefix,
  ImpegnatoSpecialStoreName,
  ImpegnatoRowName,
  MarketplaceStoreTotal,
} from '../../Constants/Configuration'
import { setCategorySortOrder } from '../../Slices/Kiosk/MarketplaceAvailabilitiesSlice'
import NumericCell from '../Generic/NumericCell'
import { useAppDispatch, useAppSelector } from '../../hooks'

function MarketplaceAvailabilities() {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const dataSource = useAppSelector(state => state.present.articleDetail.articleSelected.marketplaceAvailabilities)
  const tableColumns = useAppSelector(state => state.present.marketplaceAvailabilities.value.marketplaceColumns)
  const tableStoreColumns = useAppSelector(
    state => state.present.marketplaceAvailabilities.value.marketplaceStoreColumns,
  )
  const specificTableStoresColumns = useAppSelector(
    state => state.present.marketplaceAvailabilities.value.marketplaceSpecificStoresColumns,
  )
  const categorySortOrder = useAppSelector(state => state.present.marketplaceAvailabilities.value.categorySortOrder)
  const showB2BLine = useAppSelector(state => state.present.configuration.showB2BLine)

  const [searchValue, setSearchValue] = useState('')

  const filterResults = useCallback(
    results => {
      if (!searchValue || searchValue.length < 2) return results

      return results.filter(element => {
        return (
          unsensitiveCaseSearchMatch(element.category, searchValue) ||
          unsensitiveCaseSearchMatch(element.storeName, searchValue)
        )
      })
    },
    [searchValue],
  )

  const marketplaceResults = useMemo(() => {
    const marketPlaceData = dataSource && dataSource.length !== 0 ? dataSource : []
    if (tableColumns.length !== 0) {
      const marketplaceResults = marketPlaceData.map(element => {
        const allCategorySizes = []
        const impegnatoSizes = []
        if (element.specificStores && element.specificStores.length !== 0) {
          const storeWithSizesToDistinct = element.specificStores.filter(
            store => store.storeName !== ImpegnatoSpecialStoreName && store.storeName !== MarketplaceStoreTotal,
          )

          const impegnatoStoreWithSizesToDistinct = element.specificStores.filter(
            store => store.storeName === ImpegnatoSpecialStoreName,
          )

          const totalStoreWithSizesLimit =
            element.specificStores.find(x => x.storeName === MarketplaceStoreTotal)?.sizes ?? []
          storeWithSizesToDistinct.forEach(store => {
            if (store.sizes && store.sizes.length !== 0) {
              store.sizes.forEach(size => {
                const insertedSize = allCategorySizes.find(x => x.name === size.name)
                if (!insertedSize) {
                  allCategorySizes.push(size)
                }
              })
            }
          })

          impegnatoStoreWithSizesToDistinct.forEach(store => {
            if (store.sizes && store.sizes.length !== 0) {
              store.sizes.forEach(size => {
                const insertedSize = impegnatoSizes.find(x => x.name === size.name)
                if (insertedSize) {
                  insertedSize.quantity += size.quantity
                } else {
                  impegnatoSizes.push({
                    quantity: size.quantity,
                    name: size.name,
                    order: size.order,
                    storeId: size.storeId,
                  })
                }
              })
            }
          })

          const categorySizesDistinct = allCategorySizes.map(size => {
            const sizeLimit = totalStoreWithSizesLimit.find(x => size.name === x.name)
            const newSize = { ...size }
            if (sizeLimit) {
              newSize.limitWarning = sizeLimit?.limitWarning
            }

            return newSize
          })

          return {
            ...element,
            allCategorySizes: categorySizesDistinct.sort((a, b) => a.order - b.order),
            specificImpegnatoSizeTotal: impegnatoSizes.sort((a, b) => a.order - b.order),
          }
        }
      })

      return marketplaceResults
    }
    return marketPlaceData
  }, [dataSource, tableColumns.length])

  const renderTextWithLimitWarning = (text: string, showLimitWarning: boolean, elClassName: string) => {
    return (
      <div className={elClassName}>
        {text}
        {showLimitWarning ? (
          <Popover content={renderTranslation(language, TranslationKeys.availabilitiesLimit)} trigger='hover'>
            <ExclamationCircleFilled className='limit-track-icon' />
          </Popover>
        ) : null}
      </div>
    )
  }

  const columnsToRender = useMemo(() => {
    const columns = []
    if (tableColumns.length !== 0) {
      tableColumns.forEach(element => {
        columns.push({
          title:
            element.key === MarketplaceCategory ? (
              <span className='marketplace-availabilities-input-container'>
                <Input
                  variant={'borderless'}
                  placeholder={renderTranslation(
                    language,
                    TranslationKeys[MktTableColumnTranslationPrefix + setCapitalLetter(element.key)],
                  )}
                  addonBefore={<SearchOutlined className='input-search-icon' />}
                  className='marketplace-availabilities-search'
                  value={searchValue}
                  onChange={e => {
                    setSearchValue(e.target.value)
                  }}
                  onClick={stopPropagation}
                  allowClear={{ clearIcon: <CloseOutlined className='general-icon input-clear-icon' /> }}
                />
              </span>
            ) : (
              <div>
                {renderTranslation(
                  language,
                  TranslationKeys[MktTableColumnTranslationPrefix + setCapitalLetter(element.key)],
                )}
              </div>
            ),
          dataIndex: element.key,
          key: element.key,
          width: 180,
          render: (text, record, index) => {
            const isLimitWarningToSet = marketplaceResults
              .filter(item => item.category === record.category)
              .some(item =>
                item?.specificStores
                  ?.find(x => x.storeName === MarketplaceStoreTotal)
                  ?.sizes?.some(x => x.limitWarning),
              )

            const elClassName = element.key === MarketplaceCategory ? 'bold cursor-grab' : ''
            return renderTextWithLimitWarning(text, isLimitWarningToSet, elClassName)
          },
        })
      })

      //add size Columns
      const sizes = marketplaceResults.reduce((prev, curr) => {
        return curr.allCategorySizes ? prev.concat(curr.allCategorySizes) : prev
      }, [])

      sizes.forEach(size => {
        if (columns.filter(column => column.key === 'detail' + size.name).length === 0) {
          columns.push({
            title: size.name,
            dataIndex: 'detail' + size.name,
            key: 'detail' + size.name,
            width: calculateColumnWidthBasedOnText(size.name),
            align: 'center',
            className: 'detail-size ',
            order: size.order,
          })
        }
      })
    }

    columns[0] && (columns[0].order = -1)

    return columns.sort((a, b) => a.order - b.order)
  }, [language, searchValue, tableColumns, marketplaceResults])

  const marketplaceCategoriesData = useMemo(() => {
    const categoryData = []
    filterResults(marketplaceResults).forEach((element, index, self) => {
      if (
        (element.category === DefaultOrderCategoryLogistic && showB2BLine) ||
        element.category !== DefaultOrderCategoryLogistic
      ) {
        if (categoryData.filter(filterElement => filterElement.category === element.category).length === 0) {
          const result = {}
          result['rowId'] = element.category + index
          result[MarketplaceCategory] = element.category

          //TOTALE TUTTI NEGOZI SINGOLI (DISTINCT) PER CATEGORIA AL NETTO DI IMPEGNATO
          const totalResultByCategory = self.filter(x => x.category === element.category)

          const allStorenameSpecificStoreDistinctSizes = []

          totalResultByCategory.forEach(element => {
            if (element.specificStores && element.specificStores.length !== 0) {
              element.specificStores.forEach(store => {
                const insertedStore = allStorenameSpecificStoreDistinctSizes.find(x => x.storeName === store.storeName)
                if (store.storeName !== ImpegnatoSpecialStoreName && !insertedStore) {
                  if (store.sizes && store.sizes.length !== 0) {
                    store.sizes.forEach(size => {
                      const insertedSize = allStorenameSpecificStoreDistinctSizes.find(
                        x => x.name === size.name && x.storeName === store.storeName,
                      )
                      if (insertedSize) {
                        insertedSize.quantity += size.quantity
                      } else {
                        allStorenameSpecificStoreDistinctSizes.push({
                          storeName: store.storeName,
                          quantity: size.quantity,
                          name: size.name,
                          order: size.order,
                          storeId: size.storeId,
                        })
                      }
                    })
                  }
                }
              })
            }
          })

          categoryData.push(result)
        }
      }
    })

    //sort and return category as ordered
    return categoryData.sort((a, b) => {
      return categorySortOrder.indexOf(a.category) - categorySortOrder.indexOf(b.category)
    })
  }, [categorySortOrder, marketplaceResults, showB2BLine, filterResults])

  const marketplaceDatasource = useMemo(() => {
    return marketplaceCategoriesData.map(categoryData => {
      const result = { ...categoryData }
      const stores = []

      if (categoryData.category !== DefaultOrderCategoryLogistic) {
        //set engagedRow
        //IMPEGNATO
        const engagedRow = {}
        engagedRow[MarketplaceStoreName] = renderTranslation(language, TranslationKeys.tableMarketplaceEngaged)
        engagedRow['rowKey'] = ImpegnatoRowName

        const totalImpegnatoSizes = marketplaceResults
          .filter(x => x.category === categoryData.category)
          .reduce((prev, curr) => {
            return curr.specificImpegnatoSizeTotal ? prev.concat(curr.specificImpegnatoSizeTotal) : prev
          }, [])
        const sizeNames = totalImpegnatoSizes.map(element => {
          return element.name
        })
        const sizeDistinct = sizeNames.filter(filterGeneralDistinct)
        sizeDistinct.forEach(size => {
          const impegnatoSizeCalc = totalImpegnatoSizes
            .filter(value => value.name === size)
            .reduce((prev, curr) => {
              return prev + curr.quantity
            }, 0)
          engagedRow['detail' + size] = impegnatoSizeCalc
        })

        stores.push(engagedRow)
      }

      //set all rows
      filterResults(marketplaceResults).forEach(result => {
        if (categoryData.category === result.category) {
          const storeElement = {}
          const isLogistic = categoryData.category === DefaultOrderCategoryLogistic
          storeElement[MarketplaceStoreName] = result.storeName
          const storesAvailable = result.specificStores.filter(store => store.storeName !== ImpegnatoSpecialStoreName)
          storeElement['specificStores'] = storesAvailable
          storeElement['stockpoint'] = !!result.stockpoint
          storeElement['isLogistic'] = isLogistic

          const storesAvailableSizeSum = []

          storesAvailable.forEach(store => {
            if (store.sizes && store.sizes.length !== 0) {
              store.sizes.forEach(size => {
                const insertedSize = storesAvailableSizeSum.find(x => x.name === size.name)
                if (insertedSize) {
                  insertedSize.quantity += size.quantity
                } else {
                  storesAvailableSizeSum.push({
                    quantity: size.quantity,
                    name: size.name,
                    order: size.order,
                  })
                }
              })
            }
          })

          const marketplaceTotalWithLimits =
            storesAvailable.find(store => store.storeName === MarketplaceStoreTotal)?.sizes ?? []

          if (marketplaceTotalWithLimits.length > 0) {
            marketplaceTotalWithLimits.forEach(size => {
              storeElement['detail' + size.name] = size.quantity
            })
          } else {
            //RISULTATO PER STORE TOTALE ESCLUSO IMPEGNATO
            storesAvailableSizeSum.forEach(size => {
              storeElement['detail' + size.name] = size.quantity
            })
          }
          stores.push(storeElement)
        }
      })

      result['stores'] = stores

      return result
    })
  }, [language, marketplaceCategoriesData, marketplaceResults, filterResults])

  const expandedStoreRowRender = useCallback(
    (record, index, indent, expanded) => {
      const specificStoreColums = []
      const specificStoreData = []
      if (specificTableStoresColumns.length > 0) {
        specificTableStoresColumns.forEach(element => {
          specificStoreColums.push({
            title: element.key,
            dataIndex: element.key,
            key: element.key,
            width: 164,
            render: (text, record, index) => <div>{text}</div>,
            className: '',
          })
        })
      }

      const sizes = marketplaceResults
        .reduce((prev, curr) => (curr.allCategorySizes ? prev.concat(curr.allCategorySizes) : prev), [])
        .sort((a, b) => a.order - b.order)

      sizes.forEach(size => {
        if (specificStoreColums.filter(column => column.key === 'detail' + size.name).length === 0) {
          specificStoreColums.push({
            title: size.name,
            dataIndex: 'detail' + size.name,
            key: 'detail' + size.name,
            width: calculateColumnWidthBasedOnText(size.name),
            align: 'center',
            className: 'detail-size  noedit',
            render: text => <NumericCell value={text} extraClass='specific-store-box noedit neutral' />,
          })
        }
      })

      if (record.specificStores && record.specificStores.length !== 0 && !record.isLogistic) {
        record.specificStores
          .filter(element => element.storeName !== MarketplaceStoreTotal)
          .forEach((element, index) => {
            const specificStoreElement = { ...element }
            specificStoreElement['rowId'] = record.storeName + '-' + element.storeName + '-' + index
            //INDIVIDUAL
            //Can be multiple size with same name
            if (element.sizes && element.sizes.length) {
              const sizeNames = element.sizes.map(element => {
                return element.name
              })
              const sizeDistinct = sizeNames.filter(filterGeneralDistinct)
              sizeDistinct.forEach(size => {
                const totalQuantity = element.sizes
                  .filter(value => value.name === size)
                  .reduce((prev, curr) => {
                    return prev + curr.quantity
                  }, 0)
                specificStoreElement['detail' + size] = totalQuantity
              })
            }

            specificStoreData.push(specificStoreElement)
          })
      }

      return (
        <Table
          rowKey={record => record.rowId}
          id='marketplaceSpecificStoresRowTable'
          tableLayout='fixed'
          columns={specificStoreColums}
          dataSource={specificStoreData}
          pagination={false}
          showHeader={false}
          size='small'
        />
      )
    },
    [marketplaceResults, specificTableStoresColumns],
  )

  const storeRowExpandable = useCallback(record => {
    if (record.rowKey && record.rowKey === ImpegnatoRowName) {
      return false
    }
    if (record.stockpoint) {
      return false
    }
    if (record.isLogistic) {
      return false
    }

    return record.specificStores && record.specificStores.length !== 0 ? true : false
  }, [])

  const expandedMarketplaceRowRender = useCallback(
    (record, index, indent, expanded) => {
      const storeColumns = []
      const storeData = record.stores && record.stores.length !== 0 ? record.stores : []
      const sizes = marketplaceResults
        .reduce((prev, curr) => {
          return curr.allCategorySizes ? prev.concat(curr.allCategorySizes) : prev
        }, [])
        .sort((a, b) => a.order - b.order)

      if (tableStoreColumns.length !== 0) {
        tableStoreColumns.forEach(element => {
          storeColumns.push({
            title: element.key,
            dataIndex: element.key,
            key: element.key,
            width: 172,
            render: (text, record, index) => {
              const marketPlaceIndividualSizes = marketplaceResults.find(x => x.storeName === record.storeName)
              const isLimitWarningToSet = marketPlaceIndividualSizes?.specificStores
                ?.find(x => x.storeName === MarketplaceStoreTotal)
                ?.sizes?.some(x => x.limitWarning)
              const elClassName = record.rowKey && record.rowKey === ImpegnatoRowName ? 'engaged-label' : ''
              return renderTextWithLimitWarning(text, isLimitWarningToSet, elClassName)
            },
            className: ' noedit',
          })
        })
      }

      sizes.forEach(size => {
        if (storeColumns.filter(column => column.key === 'detail' + size.name).length === 0) {
          storeColumns.push({
            title: size.name,
            dataIndex: 'detail' + size.name,
            key: 'detail' + size.name,
            width: calculateColumnWidthBasedOnText(size.name),
            align: 'center',
            className: 'detail-size  noedit',
            render: (text, record) => (
              <NumericCell
                value={text}
                extraClass={`neutral ${
                  record.rowKey && record.rowKey === ImpegnatoRowName ? 'engaged-box noedit' : ''
                }`}
              />
            ),
          })
        }
      })
      return (
        <Table
          rowKey={record => record.storeName}
          id='marketplaceStoresRowTable'
          tableLayout='fixed'
          columns={storeColumns}
          dataSource={storeData}
          expandable={{
            expandedRowRender: expandedStoreRowRender,
            rowExpandable: storeRowExpandable,
            expandIcon: ({ expanded, onExpand, record }) =>
              storeRowExpandable(record) ? (
                expanded ? (
                  <div onClick={e => onExpand(record, e)}>
                    <MinusOutlined className='marketplace-minus-icon expand-element-icon' />
                  </div>
                ) : (
                  <div onClick={e => onExpand(record, e)}>
                    <PlusOutlined
                      onClick={e => onExpand(record, e)}
                      className='marketplace-plus-icon expand-element-icon'
                    />
                  </div>
                )
              ) : null,
            columnWidth: 25,
          }}
          pagination={false}
          showHeader={false}
          rowClassName={(record, index) => (!storeRowExpandable(record) ? 'hide-expand' : '')}
          size='small'
        />
      )
    },
    [marketplaceResults, tableStoreColumns, expandedStoreRowRender, storeRowExpandable],
  )

  const rowExpandable = useCallback(record => {
    return record.stores && Object.keys(record.stores).length !== 0 ? true : false
  }, [])

  const width = useMemo(() => {
    return columnsToRender.reduce((prev, curr) => {
      return prev + curr.width
    }, 0)
  }, [columnsToRender])

  const dragProps = useMemo(() => {
    return {
      onDragEnd(fromIndex, toIndex) {
        const item = marketplaceDatasource.splice(fromIndex, 1)[0]
        marketplaceDatasource.splice(toIndex, 0, item)

        const newSortOrder = marketplaceDatasource.map(element => {
          return element.category
        })

        dispatch(setCategorySortOrder(newSortOrder))
      },
      ignoreSelector: 'tr.ant-table-expanded-row',
      nodeSelector: 'tr.ant-table-row.draggable-row',
    }
  }, [dispatch, marketplaceDatasource])

  return (
    <ReactDragListView {...dragProps}>
      <Table
        rowKey={record => record.rowId}
        id='marketplaceAvailabilitiesTable'
        columns={columnsToRender}
        expandable={{
          expandedRowRender: expandedMarketplaceRowRender,
          rowExpandable: rowExpandable,
          expandIcon: ({ expanded, onExpand, record }) =>
            record.stores && Object.keys(record.stores).length !== 0 ? (
              expanded ? (
                <div onClick={e => onExpand(record, e)} className=''>
                  <CaretDownOutlined className='marketplace-caret-down caret-icon-down' />
                </div>
              ) : (
                <div onClick={e => onExpand(record, e)}>
                  <CaretRightOutlined className='marketplace-caret-right caret-icon-right' />
                </div>
              )
            ) : null,
          columnWidth: 25,
        }}
        tableLayout='fixed'
        dataSource={marketplaceDatasource}
        pagination={false}
        showSorterTooltip={false}
        size='small'
        rowClassName={(record, index) => 'draggable-row'}
        style={{ width: width }}
      />
    </ReactDragListView>
  )
}

export default MarketplaceAvailabilities
